import React, { useState, useMemo } from 'react';
import { Card } from '@heartinternet/hi-ui';
import { FormField } from '@heartinternet/hi-ui';
import { Form } from '@heartinternet/hi-ui';
import { PriceDisplay } from '@heartinternet/hi-ui';
import { Button } from '@heartinternet/hi-ui';
import { Input } from '@heartinternet/hi-ui';
import { Select } from '@heartinternet/hi-ui';
import { Text } from '@heartinternet/hi-ui';
import { Toggle } from '@heartinternet/hi-ui';
import { Icon } from '@heartinternet/hi-ui';
import { List } from '@heartinternet/hi-ui';
import { ListItem } from '@heartinternet/hi-ui';
import type { Product, PriceInfo } from '../types';

interface ExchangeMailboxCardProps {
  product: Product;
  domains: string[];
  formAction: string;
  isReseller?: boolean;
}

export const ExchangeMailboxCard: React.FC<ExchangeMailboxCardProps> = ({
  product,
  domains,
  formAction,
  isReseller,
}) => {
  const hasYearly = !!product.yearlyPrice;
  const [selectedDomain, setSelectedDomain] = useState(domains[0] || '');
  const [emailPrefix, setEmailPrefix] = useState('');
  const [billingInterval, setBillingInterval] = useState<'monthly' | 'yearly'>('monthly');

  const currentPrice = (
    billingInterval === 'yearly' && product.yearlyPrice ? product.yearlyPrice : product.price
  ) as PriceInfo;

  const displayAmount =
    isReseller && currentPrice.resellerAmount != null
      ? currentPrice.resellerAmount
      : currentPrice.amount;

  const target = useMemo(
    () => (emailPrefix && selectedDomain ? `${emailPrefix}@${selectedDomain}` : ''),
    [emailPrefix, selectedDomain]
  );

  const basketAddCode = useMemo(() => {
    const base = product.basketAddCode.replace(/\/(monthly|yearly)\//, `/${billingInterval}/`);
    return base;
  }, [product.basketAddCode, billingInterval]);

  const isValid = emailPrefix.length > 0 && selectedDomain.length > 0;

  return (
    <div className="hi-ui-extras-page__product-content">
      <Text variant="body" inline>
        {product.description}
      </Text>
      <div className="hi-ui-extras-page__specs-grid">
        <div className="hi-ui-extras-page__specs-card">
          <List className="hi-ui-extras-page__features hi-ui-extras-page__features--icons">
            {product.features.map((text, i) => (
              <ListItem key={i}>
                <Icon name="check-circle-grey" size={18} />
                {text}
              </ListItem>
            ))}
          </List>
        </div>
      </div>
      <Form action={formAction} method="post">
        <input type="hidden" name="type" value="MULTI" />
        <input type="hidden" name="submit-type" value="PURCHASE" />
        <input type="hidden" name="basket-add-code" value={basketAddCode} />
        <input type="hidden" name="target" value={target} />

        <Card variant="surface" padding="md" className="hi-ui-extras-page__card-form">
          <FormField label="Email address" fullWidth>
            <div className="hi-ui-extras-page__email-input">
              <div className="hi-ui-extras-page__email-prefix">
                <Input
                  type="text"
                  value={emailPrefix}
                  onChange={(e) => setEmailPrefix(e.target.value)}
                  placeholder="username"
                  required
                />
                <Text variant="body" className="hi-ui-extras-page__email-at">
                  @
                </Text>
              </div>
              <Select value={selectedDomain} onChange={(e) => setSelectedDomain(e.target.value)}>
                {domains.length === 0 && <option value="">No domains available</option>}
                {domains.map((domain) => (
                  <option key={domain} value={domain}>
                    {domain}
                  </option>
                ))}
              </Select>
            </div>
          </FormField>

          {hasYearly && (
            <div className="hi-ui-extras-page__billing-toggle">
              <Text variant="body">Monthly</Text>
              <Toggle
                checked={billingInterval === 'yearly'}
                onChange={(e) => setBillingInterval(e.target.checked ? 'yearly' : 'monthly')}
                aria-label="Billing period"
              />
              <Text variant="body">Yearly</Text>
            </div>
          )}

          <div className="hi-ui-extras-page__card-footer">
            <PriceDisplay
              amount={displayAmount}
              currency={currentPrice.currency}
              interval={currentPrice.interval}
              intervalFormat="long"
              size="lg"
            />
            <Button type="submit" variant="primary" disabled={!isValid}>
              Add to basket
            </Button>
          </div>
        </Card>
      </Form>
    </div>
  );
};
